import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, CheckCircle, Mail } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface InfoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  topic: string;
  category?: string;
}

const InfoRequestModal: React.FC<InfoRequestModalProps> = ({ isOpen, onClose, topic, category }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !name.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-info-request', {
        body: {
          email: email.trim(),
          name: name.trim(),
          topic,
          category: category || 'General',
          message: message.trim()
        }
      });

      if (error) throw error;

      setIsSuccess(true);
      toast.success('Your request has been submitted successfully!');
      
      // Reset and close after delay
      setTimeout(() => {
        setEmail('');
        setName('');
        setMessage('');
        setIsSuccess(false);
        onClose();
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting info request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setEmail('');
      setName('');
      setMessage('');
      setIsSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-delphi-dark border border-delphi-gray/30 max-w-md">
        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-delphi-light mb-2">Request Submitted!</h3>
            <p className="text-delphi-light/70 text-center">
              We'll get back to you shortly with more information.
            </p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-delphi-light flex items-center gap-2">
                <Mail className="w-5 h-5 text-delphi-red" />
                Request Information
              </DialogTitle>
              <DialogDescription className="text-delphi-light/70">
                Enter your contact details to receive more information about:
                <span className="block mt-1 text-delphi-light font-medium">{topic}</span>
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-delphi-light">
                  Full Name <span className="text-delphi-red">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="bg-delphi-gray/20 border-delphi-gray/30 text-delphi-light"
                  disabled={isSubmitting}
                  maxLength={100}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-delphi-light">
                  Email Address <span className="text-delphi-red">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="bg-delphi-gray/20 border-delphi-gray/30 text-delphi-light"
                  disabled={isSubmitting}
                  maxLength={255}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-delphi-light">
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Any specific questions or areas of interest..."
                  className="bg-delphi-gray/20 border-delphi-gray/30 text-delphi-light min-h-[80px]"
                  disabled={isSubmitting}
                  maxLength={1000}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1 border-delphi-gray/30 text-delphi-light hover:bg-delphi-gray/20"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-delphi-red hover:bg-red-600 text-white"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InfoRequestModal;
